import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_LOGIN_IMAGE = "https://customer-assets.emergentagent.com/job_3e766658-e246-462d-8176-2f4f1f18a75d/artifacts/fhfxwukd_Opswarex%20Banner.png";

const DEFAULT_SETTINGS = {
  login_background: null,
  login_title: 'Business Operations,',
  login_subtitle: 'Simplified & Streamlined',
  login_description: 'All-in-One Operations Management Platform',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(DEFAULT_LOGIN_IMAGE);
  const [loginText, setLoginText] = useState(DEFAULT_SETTINGS);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    // Fetch settings to get custom login background and text.
    // IMPORTANT: never let this block the form from rendering. On slow iOS
    // connections the page used to show only a spinner forever — users
    // thought the app was broken. We give it 3s, then show the form with
    // defaults no matter what.
    let resolved = false;
    const applyDefaults = () => {
      if (resolved) return;
      resolved = true;
      setSettingsLoaded(true);
    };
    const fallbackTimer = setTimeout(applyDefaults, 3000);

    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/settings/public`, {
          timeout: 5000,
        });
        if (response.data?.login_background) {
          setBackgroundImage(response.data.login_background);
        }
        setLoginText({
          login_title: response.data?.login_title || DEFAULT_SETTINGS.login_title,
          login_subtitle: response.data?.login_subtitle || DEFAULT_SETTINGS.login_subtitle,
          login_description: response.data?.login_description || DEFAULT_SETTINGS.login_description,
        });
      } catch (error) {
        // Use defaults if settings fetch fails
        console.log('Using default login settings');
      } finally {
        clearTimeout(fallbackTimer);
        if (!resolved) {
          resolved = true;
          setSettingsLoaded(true);
        }
      }
    };
    fetchSettings();

    return () => clearTimeout(fallbackTimer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success(`Welcome back, ${user.full_name}!`);
      // Hard navigation (full page reload) so any leftover in-memory
      // state from a previous session in this same tab — e.g. another
      // tenant's chats, polling intervals, cached analytics — is wiped
      // clean. SPA `navigate()` alone leaves component state mounted.
      window.location.replace('/dashboard');
    } catch (error) {
      // Surface backend's specific message when available, otherwise
      // classify by status: 401/403 → bad creds, anything else →
      // transient server issue. Avoids the misleading "Invalid
      // credentials" toast users see when the backend is just slow.
      const status = error?.response?.status;
      const serverDetail = error?.response?.data?.detail;
      let msg;
      if (serverDetail) {
        msg = serverDetail;
      } else if (status === 401 || status === 403) {
        msg = 'Invalid email or password';
      } else if (status && status >= 500) {
        msg = 'Server is busy. Please try again in a moment.';
      } else if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        msg = 'Network is slow. Please check your connection and retry.';
      } else {
        msg = error.message || 'Login failed. Please try again.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while settings are being fetched
  if (!settingsLoaded) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg grain-overlay flex flex-col lg:flex-row">
      {/* Mobile Header with Background Image */}
      <div className="lg:hidden relative h-48 sm:h-64 overflow-hidden">
        <img
          src={backgroundImage}
          alt="Opswarex"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-wt-bg/60 to-wt-bg" />
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="font-playfair text-xl sm:text-2xl font-bold text-wt-text-primary">
            {loginText.login_title}
            <span className="text-wt-gold"> {loginText.login_subtitle}</span>
          </h2>
          <p className="text-wt-text-secondary text-sm mt-1">
            {loginText.login_description}
          </p>
        </div>
      </div>

      {/* Desktop Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-wt-gold/20 via-transparent to-transparent" />
        <img
          src={backgroundImage}
          alt="Opswarex"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-wt-bg via-wt-bg/50 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-lg">
          <h2 className="font-playfair text-4xl font-bold text-wt-text-primary mb-4">
            {loginText.login_title}
            <br />
            <span className="text-wt-gold">{loginText.login_subtitle}</span>
          </h2>
          <p className="text-wt-text-secondary text-lg">
            {loginText.login_description}
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-6 sm:mb-8">
            <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-wt-text-primary mb-2">
              Welcome Back
            </h1>
            <p className="text-wt-text-secondary text-sm sm:text-base">
              Sign in to access your command center
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-testid="login-form">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-wt-text-primary mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full h-11 sm:h-12 rounded-lg border border-wt-border bg-wt-bg px-4 text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-gold focus:outline-none focus:ring-1 focus:ring-wt-gold smooth-transition text-base"
                placeholder="your.email@woodentwist.ae"
                data-testid="email-input"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-wt-text-primary mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full h-11 sm:h-12 rounded-lg border border-wt-border bg-wt-bg px-4 pr-12 text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-gold focus:outline-none focus:ring-1 focus:ring-wt-gold smooth-transition text-base"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary smooth-transition"
                  data-testid="toggle-password"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 sm:h-12 bg-wt-gold text-black hover:bg-wt-gold-hover font-semibold rounded-lg smooth-transition active:scale-95"
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
            <ForgotPasswordBlock />
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;

// --- Forgot password block (inline component) ----------------------------
const ForgotPasswordBlock = () => {
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email });
      toast.success('If the email exists, a reset link has been sent.');
      setOpen(false);
      setEmail('');
    } catch {
      toast.error('Could not process request');
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-wt-text-muted hover:text-wt-gold smooth-transition"
          data-testid="login-forgot-password-btn"
        >
          Forgot your password?
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 p-3 rounded-lg border border-wt-border bg-black/20 space-y-2" data-testid="forgot-password-panel">
      <div className="text-xs text-wt-text-muted">Enter your email. We'll send you a reset link.</div>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        placeholder="you@company.com"
        className="w-full px-3 py-2 rounded-md bg-black/30 border border-wt-border text-white text-sm focus:outline-none focus:border-wt-gold"
        data-testid="forgot-password-email"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex-1 py-2 rounded-md border border-wt-border text-xs text-white hover:bg-white/5"
        >Cancel</button>
        <button
          type="button"
          onClick={submit}
          disabled={sending || !email}
          className="flex-1 py-2 rounded-md bg-wt-gold text-black text-xs font-semibold disabled:opacity-50"
          data-testid="forgot-password-send"
        >{sending ? 'Sending…' : 'Send reset link'}</button>
      </div>
    </div>
  );
};
